import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Flashcard, Deck } from '../types'
import { apiClient } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import FlashcardComponent from './FlashcardComponent'
import './VocabularyList.css'

// Removal failures, in Japanese for the UI (mirrors DeckManagement's
// per-component mapper; default covers network/unknown errors).
function mapRemoveErrorToJapanese(err: any): string {
  const status = err?.response?.status
  if (status === 401) return 'ログインが必要です。再度サインインしてください。'
  if (status === 403) return 'この操作を行う権限がありません。'
  if (status === 404) return 'このカードまたはデッキはすでに削除されています。'
  return '削除できませんでした。もう一度お試しください。'
}

interface VocabularyListProps {
  deckId?: string
}

const VocabularyList: React.FC<VocabularyListProps> = ({ deckId: propDeckId }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlDeckId = searchParams.get('deck') || undefined
  const deckId = propDeckId || urlDeckId
  // The stale-link fallback only applies to URL-driven decks: a propDeckId
  // caller owns its own navigation, so we never rewrite its URL.
  const deckFromUrl = propDeckId ? undefined : urlDeckId
  const { user } = useAuth()
  const [flashcards, setFlashcards] = useState<Flashcard[]>([])
  const [decks, setDecks] = useState<Deck[]>([])
  // The deck selector needs the deck list on every visit. It loads in
  // parallel with the cards so card display never waits for it.
  const [decksLoading, setDecksLoading] = useState(true)
  const [decksFailed, setDecksFailed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCard, setSelectedCard] = useState<Flashcard | null>(null)
  const [isCardModalOpen, setIsCardModalOpen] = useState(false)
  // デッキから削除 (remove from deck) — confirm step inside the card modal.
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)

  const resetRemoveState = () => {
    setConfirmingRemove(false)
    setRemoveError(null)
  }

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'created_at' | 'front_text'>('created_at')

  // Bound so the effect can key on the id: if auth hydrates late, the id
  // changing re-runs the (idempotent) deck fetch.
  const userId = user?.user_id

  useEffect(() => {
    let cancelled = false
    const loadDecks = async () => {
      if (!userId) {
        setDecks([])
        setDecksLoading(false)
        return
      }
      // Re-arm on every real attempt: the null→id hydration path leaves
      // decksLoading false during the fetch, and a failed fetch must not
      // latch decksFailed for the retry triggered by a new userId.
      setDecksLoading(true)
      setDecksFailed(false)
      try {
        const res = await apiClient.getDecks()
        if (!cancelled) setDecks(res.data ?? [])
      } catch (err) {
        // Spec: cards still display; the selector just stays disabled.
        console.error('Error loading decks:', err)
        if (!cancelled) setDecksFailed(true)
      } finally {
        if (!cancelled) setDecksLoading(false)
      }
    }
    loadDecks()
    return () => {
      cancelled = true
    }
  }, [userId])

  // Bumped on every cards load; responses from superseded loads are dropped.
  const cardsRequestIdRef = useRef(0)

  useEffect(() => {
    // A deck switch invalidates whatever card the modal was showing: close it
    // rather than letting it remount over the new deck's grid (the select is
    // still tabbable behind the overlay until modals get a focus trap).
    setIsCardModalOpen(false)
    setSelectedCard(null)
    resetRemoveState()
    loadVocabulary()
  }, [deckId])

  // Silent auto-correction must not leave the bogus URL in history: replace,
  // not push, so Back returns to wherever the user came from. Note: RR's
  // functional setSearchParams is a render-closure read, not an atomic
  // read-modify-write — today the loading gate + discrete-event flushing
  // make that safe here; don't add a second racing writer.
  const stripDeckParam = () =>
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete('deck')
        return next
      },
      { replace: true }
    )

  // A stale ?deck= link must not dead-end: if the deck list loads and the id
  // is not in it, go back to the unfiltered view. (The 404 from the cards
  // fetch is the other signal; whichever reveals the missing deck first wins.)
  useEffect(() => {
    if (decksLoading || !deckFromUrl) return
    if (decks.length > 0 && !decks.some(d => d.deck_id === deckFromUrl)) {
      stripDeckParam()
    }
  }, [decks, decksLoading, deckFromUrl, setSearchParams])

  const loadVocabulary = async (requestId: number = ++cardsRequestIdRef.current) => {
    setLoading(true)
    setError(null)

    try {
      let cards: Flashcard[] = []
      if (deckId) {
        try {
          const response = await apiClient.getFlashcardsByDeck(deckId)
          cards = response.data ?? []
        } catch (err: any) {
          if (requestId !== cardsRequestIdRef.current) return // a newer load superseded this one
          if (err?.response?.status === 404 && deckFromUrl) {
            // Unknown/deleted deck from a stale link: fall back to the
            // unfiltered view instead of dead-ending on an error page.
            // Strip the param and let the deckId effect re-run this loader.
            stripDeckParam()
            return // loading stays true; the re-run supersedes this load
          }
          throw err
        }
      } else {
        // One fetch for the unfiltered view. The old deck-by-deck loop read
        // the still-empty `decks` closure and showed zero cards on first load.
        const response = await apiClient.getFlashcards()
        cards = response.data ?? []
      }
      if (requestId !== cardsRequestIdRef.current) return // a newer load superseded this one

      // Apply search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase()
        cards = cards.filter(card =>
          card.front_text.toLowerCase().includes(query) ||
          (card.meaning && card.meaning.toLowerCase().includes(query)) ||
          (card.reading && card.reading.toLowerCase().includes(query))
        )
      }

      // Sort cards
      cards.sort((a, b) => {
        if (sortBy === 'created_at') {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        } else {
          return a.front_text.localeCompare(b.front_text)
        }
      })

      setFlashcards(cards)
      setLoading(false)
    } catch (err: any) {
      if (requestId !== cardsRequestIdRef.current) return // a newer load superseded this one
      console.error('Error loading vocabulary:', err)
      setError(err.message || '単語を読み込めませんでした')
      setLoading(false)
    }
  }

  const handleDeckChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value) {
        next.set('deck', value)
      } else {
        next.delete('deck')
      }
      return next
    })
  }

  // The deck list is already loaded for the selector, so the confirm step can
  // name the deck without an extra fetch. Falls back when it isn't available.
  const deckName = deckId ? decks.find(d => d.deck_id === deckId)?.name : undefined

  const handleCardClick = (card: Flashcard) => {
    resetRemoveState()
    setSelectedCard(card)
    setIsCardModalOpen(true)
  }

  const closeCardModal = () => {
    if (removing) return
    setIsCardModalOpen(false)
    resetRemoveState()
  }

  const confirmRemoveFromDeck = async () => {
    if (!selectedCard || !deckId || removing) return
    setRemoving(true)
    setRemoveError(null)
    try {
      await apiClient.removeCardFromDeck(deckId, selectedCard.card_id)
      // Membership-only removal: splice locally, no refetch. card_count on
      // the deck list resyncs on its next load (not displayed on this page).
      const removedId = selectedCard.card_id
      setFlashcards(prev => prev.filter(c => c.card_id !== removedId))
      setIsCardModalOpen(false)
      setSelectedCard(null)
      resetRemoveState()
    } catch (err: any) {
      setRemoveError(mapRemoveErrorToJapanese(err))
    } finally {
      setRemoving(false)
    }
  }

  const getTagColor = (tag: string) => {
    // JLPT level colors
    if (tag === 'N5') return '#4CAF50'
    if (tag === 'N4') return '#8BC34A'
    if (tag === 'N3') return '#FFC107'
    if (tag === 'N2') return '#FF9800'
    if (tag === 'N1') return '#F44336'
    // Type colors
    if (tag === 'vocabulary') return '#2196F3'
    if (tag === 'kanji') return '#9C27B0'
    if (tag === 'grammar') return '#FF5722'
    // Default
    return '#9E9E9E'
  }

  if (loading) {
    return (
      <div className="vocabulary-list">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>単語を読み込み中...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="vocabulary-list">
        <div className="error-container">
          <h2>単語の読み込みエラー</h2>
          <p>{error}</p>
          <button onClick={() => loadVocabulary()} className="retry-btn">再試行</button>
        </div>
      </div>
    )
  }

  return (
    <div className="vocabulary-list">
      <div className="vocabulary-header">
        <h1>単語リスト</h1>
        <p>日本語の語彙を探索・管理</p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="単語を検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label htmlFor="deck-filter">デッキ:</label>
            <select
              id="deck-filter"
              className="filter-select"
              value={deckId ?? ''}
              disabled={decksLoading || decksFailed}
              onChange={handleDeckChange}
            >
              {decksLoading || decksFailed ? (
                <option value="">
                  {decksFailed ? 'デッキ一覧を取得できませんでした' : '読み込み中...'}
                </option>
              ) : (
                <>
                  <option value="">すべてのデッキ</option>
                  {decks.map(deck => (
                    <option key={deck.deck_id} value={deck.deck_id}>
                      {deck.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className="filter-group">
            <label>並び替え:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'created_at' | 'front_text')}
              className="filter-select"
            >
              <option value="created_at">新しい順</option>
              <option value="front_text">五十音順</option>
            </select>
          </div>
        </div>
      </div>

      {/* Flashcards grid */}
      <div className="flashcards-grid">
        {flashcards.length === 0 ? (
          <div className="no-cards">
            <p>単語が見つかりません。検索条件やフィルターを調整してみてください。</p>
          </div>
        ) : (
          flashcards.map(card => (
            <div
              key={card.card_id}
              className="flashcard-card"
              onClick={() => handleCardClick(card)}
            >
              <div className="flashcard-preview">
                <div className="card-header">
                  {card.tags.slice(0, 3).map(tag => (
                    <span
                      key={tag}
                      className="tag-badge"
                      style={{ backgroundColor: getTagColor(tag) }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="card-content">
                  <div className="front-text">{card.front_text}</div>
                  <div className="back-text">{card.back_text}</div>
                  {card.reading && (
                    <div className="reading">{card.reading}</div>
                  )}
                  {card.meaning && (
                    <div className="meaning">{card.meaning}</div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Card detail modal. In a deck-filtered view it also offers
          removing the card from THAT deck (membership only — the card
          itself and its other decks are untouched). */}
      {isCardModalOpen && selectedCard && (
        <div className="modal-overlay" onClick={closeCardModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeCardModal} disabled={removing}>
              ×
            </button>
            {confirmingRemove ? (
              <div className="card-remove-confirm">
                <p className="card-remove-confirm-text">
                  {/* One template literal — JSX would otherwise get to decide
                      how the line breaks render, and the test asserts the
                      exact sentence. Falls back to このデッキ when the deck
                      list is unavailable, so 「」 never renders empty. */}
                  {`「${selectedCard.front_text}」を${
                    deckName ? `デッキ「${deckName}」` : 'このデッキ'
                  }から削除しますか？`}
                </p>
                <p className="card-remove-confirm-note">
                  ※カード自体は削除されず、他のデッキには残ります
                </p>
                {removeError && <div className="card-remove-error">{removeError}</div>}
                <div className="card-remove-confirm-actions">
                  <button
                    type="button"
                    className="card-remove-cancel-btn"
                    onClick={() => resetRemoveState()}
                    disabled={removing}
                  >
                    キャンセル
                  </button>
                  <button
                    type="button"
                    className="card-remove-confirm-btn"
                    onClick={confirmRemoveFromDeck}
                    disabled={removing}
                  >
                    {removing ? '削除中...' : '削除する'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <FlashcardComponent card={selectedCard} />
                {deckId && (
                  <div className="card-modal-actions">
                    <button
                      type="button"
                      className="card-remove-btn"
                      onClick={() => {
                        setRemoveError(null)
                        setConfirmingRemove(true)
                      }}
                    >
                      デッキから削除
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default VocabularyList